import { loadTranslations } from '@angular/localize';
import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE_NAME,
  isAppLocale,
  type AppLocale,
} from './app/core/locale/app-locale';

/**
 * Standalone copy of `readCookie()` from the API interceptor. Importing that
 * module here would drag Angular into the pre-boot path, and everything Angular
 * touches may evaluate `$localize` — see `main()` below.
 */
function readLocaleCookie(): AppLocale {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE_NAME}=([^;]*)`));
  const value = match ? decodeURIComponent(match[1]) : null;
  return isAppLocale(value) ? value : DEFAULT_LOCALE;
}

/** Shape of the generated catalogues; asserted because an empty one infers as `{}`. */
interface MessageCatalogue {
  locale: string;
  translations: Record<string, string>;
}

/**
 * German is the source language, so `$localize` renders it as authored and no
 * file is fetched. The catalogue is imported rather than requested, which makes
 * it a lazy chunk: a German visitor never downloads the English messages.
 */
async function loadLocale(locale: AppLocale): Promise<void> {
  if (locale === DEFAULT_LOCALE) {
    return;
  }

  const { translations } = (await import('./locale/messages.en.json')).default as MessageCatalogue;

  // Untranslated entries are empty strings, and `loadTranslations()` would
  // render them as such. Dropping them lets the id fall through to its German
  // source instead, so a partly translated catalogue is safe to ship.
  loadTranslations(
    Object.fromEntries(Object.entries(translations).filter(([, message]) => message !== '')),
  );
}

async function main(): Promise<void> {
  const locale = readLocaleCookie();
  await loadLocale(locale);

  // The polyfill bundle pre-sets this to the source locale; correct it.
  $localize.locale = locale;
  document.documentElement.lang = locale;

  // Deliberately dynamic. Angular hoists every template message to module
  // scope, and route breadcrumbs, nav data and the switcher option lists are
  // module-level `$localize` calls too. A static import would be hoisted above
  // `loadTranslations()` and silently leave the whole app in German.
  const { bootstrap } = await import('./bootstrap');
  await bootstrap(locale);
}

main().catch((err) => console.error(err));
